import Component from '@glimmer/component';
import type Location from 'bcl/models/location';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import NumberButton from 'bcl/components/number-button';
import { fn } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import sort from 'bcl/helpers/sort';
import eq from 'ember-truth-helpers/helpers/eq';
import type StocktakingNameComponent from 'bcl/templates/auth/stocktaking/name.gts';
import type { Store } from '@warp-drive/core';
import type StocktakingEntryModel from 'bcl/models/stocktaking-entry';
import ProductGroupModel from 'bcl/models/product-group';

export interface StocktakingMobileSignature {
  // The arguments accepted by the component
  Args: {
    controller: StocktakingNameComponent;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class StocktakingMobileComponent extends Component<StocktakingMobileSignature> {
  @service declare store: Store;

  @tracked currentAmount = '';
  @tracked status = 'productGroup';
  get isSelectingProductGroup() {
    return this.status === 'productGroup';
  }
  get isSelectingItem() {
    return this.status === 'item';
  }

  @action
  updateAmount(event) {
    this.currentAmount = event.target.value;
  }
  @tracked entrySize = 'gebinde';
  @action toggleEntrySize() {
    const total = parseInt(this.currentAmount);
    if (Number.isNaN(total)) {
      if (this.currentAmount === '') {
        this.entrySize = this.entrySize === 'gebinde' ? 'einzel' : 'gebinde';
        return;
      }
      alert('keine Zahl: ' + this.currentAmount);
      return;
    }

    if (this.entrySize === 'gebinde') {
      this.editingEntry.virtual_amount = total;
      this.currentAmount = this.editingEntry.virtual_amount_single.toString();
    } else {
      this.editingEntry.virtual_amount_single = total;
      this.currentAmount = this.editingEntry.virtual_amount.toString();
    }
    if (this.currentAmount === '0') {
      this.currentAmount = '';
    }
    this.entrySize = this.entrySize === 'gebinde' ? 'einzel' : 'gebinde';
  }
  @action addNumber(num: number | string) {
    if (num === 'del') {
      this.currentAmount = this.currentAmount.slice(
        0,
        this.currentAmount.length - 1
      );
      return;
    }
    if (num === 'done') {
      if (this.currentAmount === '') {
        if (this.status === 'entering_global') {
          this.status = 'productGroup';
        } else {
          this.status = 'item';
        }
        return;
      }
      const total = parseInt(this.currentAmount);
      if (Number.isNaN(total)) {
        alert('keine Zahl: ' + this.currentAmount);
        return;
      }

      if (this.entrySize === 'gebinde') {
        this.editingEntry.virtual_amount = total;
      } else {
        this.editingEntry.virtual_amount_single = total;
      }
      this.currentAmount = '';
      if (this.status === 'entering_global') {
        this.status = 'productGroup';
      } else {
        this.status = 'item';
      }

      return;
    } else {
      this.currentAmount += num;
    }
  }
  get entries() {
    const entries = [...this.args.controller.entries];
    entries.sort((a, b) =>
      a.location_item.item.name.localeCompare(b.location_item.item.name)
    );
    return entries;
  }

  get notInsertedEntries() {
    return this.entries.filter((ste) => {
      return !ste.hasSetAmount;
    });
  }
  get insertedEntries() {
    return this.entries.filter((ste) => {
      return ste.hasSetAmount;
    });
  }
  get entriesForProductGroup() {
    return (
      this.entries.filter(
        (ste) => ste.item.product_group === this.args.controller.productGroup
      ) || []
    );
  }

  get notInsertedPGEntries() {
    return this.entriesForProductGroup.filter((ste) => !ste.hasSetAmount) || [];
  }
  get insertedPGEntries() {
    return this.entriesForProductGroup.filter((ste) => ste.hasSetAmount) || [];
  }
  @tracked declare editingEntry?: StocktakingEntryModel;
  @action editNotInsertedItem(idx: number) {
    this.editingEntry = this.notInsertedPGEntries[idx];
    this.status = 'entering';
    this.entrySize = 'gebinde';
  }
  @action editCheckedItem(idx: number) {
    this.editingEntry = this.insertedPGEntries[idx];
    this.currentAmount = this.editingEntry?.virtual_amount.toString() ?? '0';
    this.status = 'entering';
    this.entrySize = 'gebinde';
  }
  @action editGlobalEntryNotInserted(idx: number) {
    this.editingEntry = this.notInsertedEntries[idx];
    this.currentAmount = this.editingEntry?.virtual_amount.toString() ?? '0';
    this.status = 'entering_global';
    this.entrySize = 'gebinde';
  }
  @action editGlobalEntryInserted(idx: number) {
    this.editingEntry = this.insertedEntries[idx];
    this.currentAmount = this.editingEntry?.virtual_amount.toString() ?? '0';
    this.status = 'entering_global';
    this.entrySize = 'gebinde';
  }
  get productGroups() {
    const pgs = [...this.store.peekAll<ProductGroupModel>('product-group')];
    return pgs.sort((a: ProductGroupModel, b: ProductGroupModel) =>
      a.name.localeCompare(b.name)
    );
  }
  @action selectProductGroup(productGroup: ProductGroupModel) {
    this.args.controller.changeProductGroup(productGroup);
    this.status = 'item';
  }
  @action switchToProductGroupSelection() {
    this.status = 'productGroup';
  }
  @action leave() {
    this.args.controller.changeProductGroup(null);
    this.args.controller.toggleFastEntry();
  }

  <template>
    {{#if this.isSelectingProductGroup}}
      {{#each this.productGroups as |productGroup|}}
        <button
          type="button"
          data-test-productGroup-button
          class="items-center mt-2 ml-2 p-2 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          {{on "click" (fn this.selectProductGroup productGroup)}}
        >
          {{productGroup.name}}
        </button>
      {{else}}
        keine Produktgruppen
      {{/each}}
      <button
        type="button"
        data-test-leave-button
        class="text-center mt-2 ml-2 p-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        {{on "click" this.leave}}
      >
        verlassen
      </button>
      <div class="mt-6 grid grid-cols-2">
        {{#each
          (sort this.notInsertedEntries "location_item.item.name")
          as |entry idx|
        }}
          <button
            type="button"
            data-test-check-item
            class="max-w-[10em] text-center truncate mt-2 ml-2 items-center px-2 py-2 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            {{on "click" (fn this.editGlobalEntryNotInserted idx)}}
          >
            {{#if entry.location_item.item.short_name}}
              {{entry.location_item.item.short_name}}
            {{else}}
              {{entry.location_item.item.name}}
            {{/if}}
          </button>
        {{/each}}
        {{#each
          (sort this.insertedEntries "location_item.item.name")
          as |entry idx|
        }}

          <button
            type="button"
            data-test-check-item
            class="max-w-[10em] text-center truncate mt-2 ml-2 items-center px-2 py-2 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            {{on "click" (fn this.editGlobalEntryInserted idx)}}
          >
            ({{entry.virtual_amount}}K
            {{entry.virtual_amount_single}}F)
            {{#if entry.location_item.item.short_name}}
              {{entry.location_item.item.short_name}}
            {{else}}
              {{entry.location_item.item.name}}
            {{/if}}
          </button>
        {{/each}}
      </div>

    {{else if this.isSelectingItem}}

      {{#each
        (sort this.notInsertedPGEntries "location_item.item.name")
        as |entry idx|
      }}
        <button
          type="button"
          data-test-check-item
          class="max-w-[10em] truncate text-center w-full mt-2 ml-2 items-center px-2 py-2 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          {{on "click" (fn this.editNotInsertedItem idx)}}
        >
          {{#if entry.location_item.item.short_name}}
            {{entry.location_item.item.short_name}}
          {{else}}
            {{entry.location_item.item.name}}
          {{/if}}
        </button>

      {{else}}
        keine nicht eingetragene Artikel
      {{/each}}
      <button
        type="button"
        data-test-leave-button
        class="max-w-[10em] truncate mt-2 ml-2 items-center px-2 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        {{on "click" this.switchToProductGroupSelection}}
      >
        verlassen
      </button>
      <div class="mt-1">&nbsp;</div>
      {{#each
        (sort this.insertedPGEntries "location_item.item.name")
        as |entry idx|
      }}
        <button
          data-test-check-checked-item
          type="button"
          class="max-w-[10em] truncate mt-2 ml-2 w-full items-center px-2 py-2 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          {{on "click" (fn this.editCheckedItem idx)}}
        >
          ({{entry.virtual_amount}}K
          {{entry.virtual_amount_single}}F)

          {{#if entry.location_item.item.short_name}}
            {{entry.location_item.item.short_name}}
          {{else}}
            {{entry.location_item.item.name}}
          {{/if}}
        </button>

      {{else}}

        keine eingetragene Artikel
      {{/each}}

    {{else}}
      <h2 class="text-xl inline">
        {{#if this.editingEntry.item.short_name}}
          {{this.editingEntry.item.short_name}}
        {{else}}
          {{this.editingEntry.item.name}}
        {{/if}}
        <span class="ml-2">

          ({{#if (eq this.entrySize "gebinde")}}Gebinde{{else}}Einzel{{/if}})
          <button
            type="button"
            data-test-leave-button
            class="max-w-[10em] truncate mt-2 items-center px-2 py-2 text-base font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            {{on "click" this.toggleEntrySize}}
          >
            wechsel
          </button>
        </span>
      </h2>
      <label class="block mt-2">
        <input
          class="text-black"
          type="number"
          value={{this.currentAmount}}
          {{on "input" this.updateAmount}}
        />
      </label>
      <table class="mt-4">
        <tbody>

          <tr>
            <td>
              <NumberButton
                data-test-1-button
                @addNumber={{this.addNumber}}
                @num={{1}}
              />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{2}} />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{3}} />
            </td>
          </tr>
          <tr>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{4}} />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{5}} />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{6}} />
            </td>
          </tr>
          <tr>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{7}} />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{8}} />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{9}} />
            </td>
          </tr>
          <tr>
            <td>
              <NumberButton
                data-test-del-button
                @addNumber={{this.addNumber}}
                @num="del"
                @display="&lt;"
              />
            </td>
            <td>
              <NumberButton @addNumber={{this.addNumber}} @num={{0}} />
            </td>
            <td>
              <NumberButton
                data-test-done-button
                @addNumber={{this.addNumber}}
                @num="done"
                @display="&#10003;"
              />
            </td>
          </tr>
        </tbody>
      </table>
    {{/if}}
  </template>
}
